import React, { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import Image from "../Tags/Image";
import { NavLink, useNavigate } from "react-router-dom";
import { useCaptureEvent } from "../../hooks/useCaptureEvent";
import Input from "../Tags/Input";
import { Search } from "lucide-react";
import useModal from "../GenericModal/useModal";
import GenericModal from "../GenericModal";
import InviteModal from "../containers/Invitations/InviteModal";
import EllipsisText from "../EllipsisText";
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function WorkspaceDropdown({ activeWorkspace, workspaces, switchWorkspace, sidebarMini }) {
  const { isOpen, openModal, closeModal } = useModal(false);
  const captureEvent = useCaptureEvent();
  const [searchQuery, setSearchQuery] = useState("");
  const hasManyWorkspaces = workspaces && workspaces.length > 5;
  const navigate = useNavigate();

  const filteredWorkspaces = hasManyWorkspaces
    ? workspaces.filter((workspace) => workspace.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : workspaces;

  return (
    <>
      <Menu as="div" className="relative block text-left">
        <div>
          <Menu.Button
            className={`flex items-center justify-between rounded-xl w-full text-text-600 font-semibold border border-gray-200  ${sidebarMini ? "px-1 pl-2.5 py-2.5" : "px-3 py-2.5"} bg-gray-50`}
            onClick={() => {
              captureEvent("TOP_SwitchWorkspaceBtn");
            }}
          >
            <div className="inline-flex items-center">
              {activeWorkspace.icon ? (
                <Image
                  src={activeWorkspace.icon}
                  className="w-[24px] h-[24px] rounded-full no-invert"
                  width={24}
                  height={24}
                  nobaseurl
                />
              ) : (
                <Image src={`/images/icons/untitledui/org.svg`} className="" width={24} height={24} />
              )}
              {!sidebarMini && (
                <span className="ml-2 whitespace-pre">
                  <EllipsisText charLimit={16} className="cursor-pointer">
                    {activeWorkspace.name}
                  </EllipsisText>
                </span>
              )}
            </div>
            {!sidebarMini && <Image src="/images/icons/arrow-down.svg" className="ml-3" />}
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className="absolute left-0 z-10 mt-2 w-56 origin-top-right 
            divide-y divide-border rounded-md bg-background shadow-lg 
            ring-1 ring-border ring-opacity-5 focus:outline-none"
          >
            {activeWorkspace && (activeWorkspace.role === "Owner" || activeWorkspace.role === "Admin") && (
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => {
                        if (!activeWorkspace?.slug) {
                          return;
                        }
                        captureEvent("TOP_WorkspaceDropdown_WorkspaceSettingsBtn");
                        if (typeof window !== "undefined") {
                          window.dispatchEvent(new Event("openWorkspaceSettingsMenu"));
                        }
                        navigate(`/${activeWorkspace.slug}/settings/general`);
                      }}
                      className={classNames(
                        active ? "bg-secondary" : "",
                        "flex items-center space-x-2 w-full px-4 py-2 text-left"
                      )}
                    >
                      <svg
                        width="14"
                        height="16"
                        viewBox="0 0 14 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.26344 12.9141L5.65307 13.7904C5.7689 14.0513 5.95792 14.2729 6.19722 14.4284C6.43652 14.584 6.71581 14.6668 7.00122 14.6667C7.28663 14.6668 7.56592 14.584 7.80522 14.4284C8.04452 14.2729 8.23354 14.0513 8.34937 13.7904L8.739 12.9141C8.8777 12.6032 9.111 12.3439 9.40567 12.1734C9.70219 12.0023 10.0452 11.9295 10.3857 11.9652L11.339 12.0667C11.6228 12.0967 11.9092 12.0438 12.1635 11.9143C12.4177 11.7848 12.629 11.5843 12.7716 11.3371C12.9144 11.0901 12.9824 10.8069 12.9674 10.522C12.9524 10.237 12.8551 9.96258 12.6871 9.73189L12.1227 8.95634C11.9217 8.67813 11.8143 8.34325 11.816 8.00004C11.816 7.65777 11.9244 7.32427 12.1257 7.04745L12.6901 6.27189C12.858 6.04121 12.9554 5.76675 12.9704 5.48182C12.9854 5.19689 12.9173 4.91373 12.7746 4.66671C12.632 4.41953 12.4207 4.21903 12.1664 4.08953C11.9121 3.96002 11.6257 3.90706 11.342 3.93708L10.3886 4.03856C10.0482 4.07431 9.70515 4.00145 9.40863 3.83041C9.11338 3.65888 8.88001 3.39828 8.74196 3.08597L8.34937 2.20967C8.23354 1.94882 8.04452 1.72718 7.80522 1.57164C7.56592 1.4161 7.28663 1.33333 7.00122 1.33337C6.71581 1.33333 6.43652 1.4161 6.19722 1.57164C5.95792 1.72718 5.7689 1.94882 5.65307 2.20967L5.26344 3.08597C5.1254 3.39828 4.89202 3.65888 4.59678 3.83041C4.30025 4.00145 3.95722 4.07431 3.61678 4.03856L2.66048 3.93708C2.3767 3.90706 2.09031 3.96002 1.83602 4.08953C1.58174 4.21903 1.37049 4.41953 1.22789 4.66671C1.0851 4.91373 1.01708 5.19689 1.03207 5.48182C1.04705 5.76675 1.14441 6.04121 1.31233 6.27189L1.87678 7.04745C2.07807 7.32427 2.18647 7.65777 2.18641 8.00004C2.18647 8.34232 2.07807 8.67581 1.87678 8.95263L1.31233 9.72819C1.14441 9.95887 1.04705 10.2333 1.03207 10.5183C1.01708 10.8032 1.0851 11.0863 1.22789 11.3334C1.37063 11.5804 1.58191 11.7808 1.83615 11.9103C2.0904 12.0398 2.37672 12.0928 2.66048 12.063L3.61381 11.9615C3.95426 11.9258 4.29729 11.9986 4.59381 12.1697C4.89017 12.3407 5.12462 12.6014 5.26344 12.9141Z"
                          stroke="#717680"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.00003 10C8.1046 10 9.00003 9.10461 9.00003 8.00004C9.00003 6.89547 8.1046 6.00004 7.00003 6.00004C5.89546 6.00004 5.00003 6.89547 5.00003 8.00004C5.00003 9.10461 5.89546 10 7.00003 10Z"
                          stroke="#717680"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      <span>Workspace settings</span>
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  <NavLink
                    className="flex items-center space-x-2 w-full px-4 py-2 text-left"
                    onClick={() => {
                      openModal();
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.00004 10.3333H5.00004C4.06967 10.3333 3.60448 10.3333 3.22595 10.4482C2.37368 10.7067 1.70673 11.3736 1.4482 12.2259C1.33337 12.6044 1.33337 13.0696 1.33337 14M12.6667 14V10M10.6667 12H14.6667M9.66671 5C9.66671 6.65685 8.32356 8 6.66671 8C5.00985 8 3.66671 6.65685 3.66671 5C3.66671 3.34315 5.00985 2 6.66671 2C8.32356 2 9.66671 3.34315 9.66671 5Z"
                        stroke="#717680"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>

                    <span>Invite your team</span>
                  </NavLink>
                </Menu.Item>
              </div>
            )}
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <a
                    href="#none"
                    className={classNames(
                      active ? "bg-secondary" : "",
                      "flex items-center space-x-2 px-4 py-3 text-xs pointer-events-none text-foreground/60"
                    )}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.3327 10.3333H1.66602M1.66602 10.3333L4.33268 7.66667M1.66602 10.3333L4.33268 13M1.66602 3.66667H12.3327M12.3327 3.66667L9.66602 1M12.3327 3.66667L9.66602 6.33333"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span> Switch to</span>
                  </a>
                )}
              </Menu.Item>

              {hasManyWorkspaces && (
                <div className="py-1 px-4 mb-1">
                  <Input
                    icon={<Search width={14} height={14} />}
                    type="text"
                    placeholder="Search workspace"
                    className="w-full rounded-md border border-border h-9 text-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              )}
              {filteredWorkspaces && filteredWorkspaces.length > 0 && (
                <div className={hasManyWorkspaces && "h-48 overflow-y-scroll custom-scrollable-dropdown"}>
                  {filteredWorkspaces.map((workspace) => (
                    <Menu.Item key={workspace.id}>
                      {({ active }) => (
                        <button
                          onClick={() => {
                            switchWorkspace(workspace.id);
                            captureEvent("TOP_WorkspaceDropdown_SelectWorkspaceBtn");
                          }}
                          className={classNames(
                            active ? "bg-secondary" : "",
                            "flex justify-between px-4 py-2 w-full"
                          )}
                        >
                          <div className="inline-flex items-center break-all">
                            {workspace.icon ? (
                              <Image
                                src={workspace.icon}
                                className="mr-2 rounded-full no-invert"
                                width={20}
                                height={20}
                                nobaseurl
                              />
                            ) : (
                              <Image
                                src={`/images/icons/untitledui/org.svg`}
                                className="mr-2"
                                width={18}
                                height={18}
                              />
                            )}
                            {workspace.name}
                          </div>
                          {workspace.id === activeWorkspace.id && (
                            <Image
                              src="/images/icons/check-circle.svg"
                              alt=""
                              width={20}
                              height={20}
                              className="mr-2"
                            />
                          )}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              )}
              {filteredWorkspaces && filteredWorkspaces.length === 0 && (
                <div>
                  <p className="p-4">No workspace found!</p>
                </div>
              )}
            </div>

            <div className="py-1">
              <Menu.Item>
                <NavLink
                  to={`/workspaces`}
                  className="flex items-center space-x-2 w-full px-4 py-2 text-left"
                  onClick={() => {
                    captureEvent("TOP_WorkspaceDropdown_ManageWorkspaceBtn");
                  }}
                >
                  <svg
                    width="16"
                    height="15"
                    viewBox="0 0 16 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.00065 4L7.00065 7M4.00065 4H2.00065L1.33398 2L2.00065 1.33334L4.00065 2V4ZM12.84 1.82734L11.0882 3.57909C10.8242 3.8431 10.6922 3.97511 10.6428 4.12732C10.5993 4.26122 10.5993 4.40545 10.6428 4.53935C10.6922 4.69157 10.8242 4.82357 11.0882 5.08758L11.2464 5.24576C11.5104 5.50977 11.6424 5.64177 11.7946 5.69123C11.9285 5.73474 12.0728 5.73474 12.2067 5.69123C12.3589 5.64177 12.4909 5.50977 12.7549 5.24576L14.3935 3.60715C14.57 4.03659 14.6673 4.50693 14.6673 5C14.6673 7.02505 13.0257 8.66667 11.0007 8.66667C10.7565 8.66667 10.5179 8.64281 10.2872 8.59729C9.96306 8.53337 9.80101 8.50142 9.70278 8.5112C9.59834 8.52161 9.54686 8.53727 9.45432 8.58679C9.36728 8.63337 9.27997 8.72069 9.10534 8.89532L4.33398 13.6667C3.7817 14.2189 2.88627 14.2189 2.33399 13.6667C1.7817 13.1144 1.7817 12.2189 2.33399 11.6667L7.10534 6.89532C7.27997 6.72069 7.36728 6.63337 7.41386 6.54633C7.46338 6.45379 7.47904 6.40232 7.48945 6.29788C7.49924 6.19964 7.46728 6.03759 7.40336 5.7135C7.35784 5.48271 7.33398 5.24414 7.33398 5C7.33398 2.97496 8.97561 1.33334 11.0007 1.33334C11.671 1.33334 12.2993 1.51322 12.84 1.82734ZM8.00069 9.99997L11.6673 13.6666C12.2196 14.2189 13.115 14.2189 13.6673 13.6666C14.2196 13.1143 14.2196 12.2189 13.6673 11.6666L10.6509 8.6502C10.4373 8.62999 10.2291 8.59147 10.0278 8.53622C9.76847 8.46503 9.48395 8.51671 9.29376 8.70689L8.00069 9.99997Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Manage all workspaces</span>
                </NavLink>
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
      <GenericModal isOpen={isOpen} closeModal={closeModal}>
        <InviteModal closeModal={closeModal} activeWorkspace={activeWorkspace} />
      </GenericModal>
    </>
  );
}
